import { useEffect, useRef, useState } from 'react'
import { ActionBar, MenuDeLaCarte } from './ActionBar'
import { Icone } from '@/components/ui/Icone'
import type { Carte, Devoilement, MotifDeSignalement, ReponseComposee } from '@/lib/api/feed'
import type { RenduOptions } from '@/lib/content/inline'
import { RichText } from '@/lib/content/RichText'
import { ListeOrdonnable } from './ListeOrdonnable'
import { TableDAppariement } from './TableDAppariement'
import { porteDesFormules, useMath } from '@/lib/content/useMath'

/**
 * La ligne-item — 06 §6.1, RG-D12bis.
 *
 * **Une ligne, des filets, du texte. Aucune carte.** C'est le composant
 * central de l'application : le candidat en verra des centaines par semaine,
 * et chaque pixel d'ornement s'y paie en densité perdue.
 *
 * **La grammaire d'un post** (7 août 2026). Énoncé en gras, menu trois points
 * en haut pour ce qui est rare ou grave, barre d'icônes en bas pour les gestes
 * fréquents. Le candidat connaît déjà cette grammaire : la lui emprunter lui
 * épargne d'apprendre la nôtre.
 *
 * **Le verdict est instantané et local.** La carte du Fil porte ses clés
 * (`key_option_ids`), et l'écran corrige lui-même : attendre l'aller-retour
 * réseau rendait chaque réponse aussi lente que la connexion, ce qu'ADR-06
 * avait déjà refusé — « le navigateur corrige pour répondre ». Le serveur
 * re-corrige derrière, et c'est SA correction qui compte pour la maîtrise et
 * le classement. Une carte d'évaluation notée ne porte PAS de clés : elle
 * n'affiche alors aucun verdict, ce que RG-3.11quater demande précisément.
 *
 * Trois règles gouvernent son comportement, et elles se tiennent :
 *
 *   · **la réponse se verrouille à la première tape** (RG-2.3). Pas de
 *     confirmation, pas de retour en arrière : on mesure ce que le candidat
 *     sait, pas ce qu'il finit par cocher ;
 *   · **la correction s'affiche immédiatement et sans action** (RG-2.4) —
 *     verdict, bonne réponse, note de correction ;
 *   · **« Détails » n'existe qu'après avoir répondu** (RG-2.4ter) : ni
 *     visible, ni grisé, ni présent dans le DOM. Une explication de 800
 *     caractères dépliée d'office repousserait la carte suivante hors de
 *     l'écran et casserait le rythme du défilement.
 */
export function ItemLine({
  carte,
  devoilement,
  enAttente = false,
  economieDeDonnees = false,
  marque = false,
  signale = false,
  sansVerdict = false,
  verrouillee = false,
  enFile = false,
  choixInitial,
  onRepondre,
  onMarquer,
  onSignaler,
  onQuiz,
  onLecon,
  onPartager,
  onPartagerDehors,
}: {
  carte: Carte
  devoilement?: Devoilement | undefined
  enAttente?: boolean | undefined
  economieDeDonnees?: boolean | undefined
  marque?: boolean | undefined
  signale?: boolean | undefined
  /**
   * RG-3.11quater — masque TOUT retour de justesse.
   *
   * Une évaluation notée verrouille la réponse sans la colorer et sans dire
   * « juste » : la correction immédiate y transformerait l'épreuve en série
   * d'indices, et le score cesserait de mesurer quoi que ce soit.
   */
  sansVerdict?: boolean | undefined
  /**
   * La carte n'accepte plus rien — RG-2.28, [DECISIONS §106].
   *
   * **Le chronomètre expiré ne fermait pas la question.** Le quiz marquait le
   * blanc et affichait « Question suivante », mais les options restaient
   * cliquables : le candidat répondait après le temps, la réponse partait au
   * journal, et elle comptait dans le bilan. Le chronomètre ne mesurait donc
   * rien — ce qu'il existe précisément pour mesurer.
   */
  verrouillee?: boolean | undefined
  /**
   * La réponse attend le réseau — RG-9.4, [DECISIONS §111].
   *
   * La carte est close — elle a été répondue — mais sans verdict : hors
   * connexion il n'en existe pas, les cartes du Fil ne portant pas leur clé
   * (CA-4.1). Le taire laisserait croire à un envoi silencieusement perdu, ce
   * qui est exactement ce qu'on vient de cesser de faire.
   */
  enFile?: boolean | undefined
  /**
   * Ce que le candidat AVAIT répondu — RG-3.11quinquies.
   *
   * Sert à la relecture des résultats d'un quiz : sans lui, la carte relue
   * afficherait la bonne réponse en vert et rien d'autre, et le candidat ne
   * saurait pas ce qu'il avait coché. Or c'est exactement l'écart entre les
   * deux qu'il vient lire.
   */
  choixInitial?: ReponseComposee | undefined
  onRepondre: (reponse: ReponseComposee, tempsMs: number) => void
  onMarquer?: ((marque: boolean) => void) | undefined
  onSignaler?: ((motif: MotifDeSignalement) => void) | undefined
  /** RG-2.38 — atteindre le tag en une tape, pour lancer un quiz dessus. */
  onQuiz?: (() => void) | undefined
  /** RG-04-26 — la leçon qui traite le sujet, quand il y en a une. */
  onLecon?: (() => void) | undefined
  onPartager?: (() => void) | undefined
  /**
   * RG-2.47, [DECISIONS §192] — l'adresse publique de cette question.
   *
   * Dans le menu des trois points, jamais dans la barre : le candidat n'en a
   * que trois par jour, et une sixième icône sous le pouce inviterait au geste
   * d'humeur. Absent — c'est le cas partout où la carte n'est pas dans le Fil —,
   * l'entrée n'apparaît pas.
   */
  onPartagerDehors?: (() => void) | undefined
}) {
  // `choixInitial` n'est lu qu'au premier rendu, et c'est voulu : la carte
  // relue est montée une fois, avec sa réponse, et ne change plus.
  const [choisies, setChoisies] = useState<number[]>(choixInitial?.options ?? [])

  // §150 — l'appariement ne se coche pas : il associe deux colonnes. Une liste
  // d'identifiants ne peut pas porter cela, d'où un second état.
  const [paires, setPaires] = useState<Record<string, string>>(choixInitial?.paires ?? {})

  // RG-P39 — le temps sépare « su » de « retrouvé », et décide donc de la
  // date de la prochaine révision. Le compter depuis l'affichage de la carte
  // est la seule mesure honnête : le candidat lit avant de répondre.
  //
  // Posé dans un effet, pas au rendu : un rendu doit être pur, et React peut
  // en jouer plusieurs avant d'afficher quoi que ce soit.
  const montreeA = useRef<number | null>(null)

  useEffect(() => {
    montreeA.current ??= maintenant()
  }, [])

  const multiple = carte.type === 'qcm_multiple'

  // RG-2.2quinquies — l'ordonnancement a sa propre interface : on y remet des
  // éléments dans l'ordre, on n'y coche rien.
  const aOrdonner = carte.type === 'ordonnancement'

  // §150 — **l'appariement a sa propre interface** : deux colonnes, et le
  // candidat apparie. Rendu en choix multiple, il n'était plus une association
  // mais un vrai/faux à six lignes, et sa correction comptait des cases là où
  // le serveur attend un appariement.
  const aApparier = carte.type === 'appariement' && carte.appariements !== undefined

  // RG-2.2bis, RG-2.2ter — combien de bonnes réponses. Le savoir change la
  // façon de répondre : un QCM à quatre clés présenté comme un QCM simple se
  // répond faux par construction.
  //
  // **`nb_cles` d'abord.** Il arrive avec TOUTES les cartes, y compris celles
  // d'une épreuve notée, qui ne portent aucune clé (RG-3.11quater) : sans lui,
  // un choix multiple de quiz s'affichait sans son compte et se validait à
  // n'importe quel nombre. `key_option_ids` reste en second pour les cartes
  // servies avant l'ajout du champ.
  const combienDeCles = carte.nb_cles ?? carte.key_option_ids?.length ?? null

  // RG-2.3 — le verrou est LOCAL et immédiat.
  //
  // Le faire dépendre de la réponse du serveur — `devoilement`, `enAttente` —
  // laissait une fenêtre grande comme l'aller-retour réseau : sur une 3G lente,
  // le candidat qui ne voit rien se passer retape, et deux réponses partent
  // pour la même carte. La seconde est fausse la moitié du temps, et elle
  // compte.
  const [envoyee, setEnvoyee] = useState(false)

  // Le verdict que l'écran calcule lui-même, en attendant celui du serveur.
  // Il porte la couleur des options et le mot « Juste / Faux » ; la note de
  // correction et l'explication arrivent avec la réponse du serveur.
  const [local, setLocal] = useState<Devoilement | null>(null)

  // Le repli des détails vit ICI et non dans la correction : la barre
  // d'actions du bas doit pouvoir l'ouvrir.
  const [deplie, setDeplie] = useState(false)

  // `verrouillee` s'y ajoute : à l'expiration du chronomètre, la carte est
  // close au même titre qu'après une réponse (RG-2.28).
  const repondu = envoyee || verrouillee || enFile || devoilement !== undefined || enAttente

  // Le serveur fait foi dès qu'il a parlé : un désaccord se corrige en sa
  // faveur, sans le dire — le candidat n'a rien à faire de notre désaccord.
  // `sansVerdict` coupe court : dans une épreuve notée, rien ne s'affiche.
  // **Le LOCAL d'abord.** Il porte déjà la note et l'explication, arrivées avec
  // la carte ; laisser le serveur reprendre la main ferait clignoter le texte
  // quelques secondes après la réponse, sans rien y ajouter.
  const montre = sansVerdict ? undefined : (local ?? devoilement ?? undefined)

  // ADR-07 — KaTeX ne se charge que pour les cartes qui portent des formules.
  //
  // **L'indice du serveur, OU ce qui est réellement à l'écran** — §153. §140 a
  // étendu `has_math` à tout ce que la carte affiche *au moment où elle part* ;
  // mais dans une évaluation notée la correction ne part PAS avec la carte
  // (RG-3.11quater) : elle revient après la réponse, par un second aller-retour.
  // Une note de correction pleine de formules, sur un énoncé qui n'en portait
  // aucune, retombait donc exactement dans le défaut que §140 venait de fermer.
  //
  // L'indice du serveur garde son utilité : il évite un premier rendu sans
  // formules, suivi d'un second avec — c'est-à-dire un texte qui saute sous les
  // yeux du candidat au moment où il le lit.
  const rendreMath = useMath(carte.has_math || porteDesFormules(...aRendre(carte, montre)))
  const options = { economieDeDonnees, rendreMath }

  // Ce que le candidat a composé, prêt à partir — §150.
  const composee = (options: number[]): ReponseComposee =>
    aApparier ? { options, paires } : { options }

  const choisir = (id: number): void => {
    if (repondu) return

    if (aApparier || aOrdonner) return

    if (!multiple) {
      setChoisies([id])
      setEnvoyee(true)
      setLocal(sansVerdict ? null : corriger([id], carte, paires))
      onRepondre(composee([id]), depuis(montreeA.current))

      return
    }

    setChoisies((avant) => {
      if (avant.includes(id)) return avant.filter((x) => x !== id)

      const suivantes = [...avant, id]

      // RG-2.2quater — **la plus ancienne se décoche.** On pouvait cocher les
      // quatre options d'une question à deux clés ; le bouton « Valider »
      // devenait alors gris, sans un mot, et le candidat devait deviner qu'il
      // fallait décocher.
      //
      // Décocher plutôt que refuser le clic : refuser ne produit aucun retour
      // — on tape, rien ne bouge, cela se lit comme une panne. Décocher la
      // plus ancienne fait ce que le candidat voulait, « en fait c'est plutôt
      // celle-ci », et laisse le compte juste.
      if (combienDeCles === null || suivantes.length <= combienDeCles) return suivantes

      return suivantes.slice(suivantes.length - combienDeCles)
    })
  }

  return (
    <article className="border-b px-4 py-3" style={{ borderColor: 'var(--bordure)' }}>
      <div className="flex items-start justify-between gap-2">
        <Entete carte={carte} onQuiz={onQuiz} />

        {onSignaler !== undefined ? (
          <MenuDeLaCarte
            signale={signale}
            stats={carte.stats}
            onSignaler={onSignaler}
            onPartagerDehors={onPartagerDehors}
          />
        ) : null}
      </div>

      {/* L'énoncé est en GRAS, partout dans l'application (7 août 2026) : dans
          une liste dense, c'est lui qu'on cherche des yeux en défilant. */}
      <div className="mt-2 text-[15px] font-bold leading-6">
        <RichText texte={carte.stem_md} options={options} />
      </div>

      <Consigne type={carte.type} combienDeCles={combienDeCles} repondu={repondu} />

      {/* RG-2.2quinquies — **l'ordonnancement se GLISSE.** Rendu comme un choix
          multiple, il était impossible à réussir : le serveur compare la
          séquence soumise à la séquence attendue, et une liste de cases cochées
          ne porte aucun ordre. */}
      {aApparier ? (
        <TableDAppariement
          gauche={carte.options}
          droite={carte.appariements ?? []}
          options={options}
          desactivee={repondu}
          attendu={montre === undefined ? undefined : carte.key_pairs}
          onChangement={setPaires}
        />
      ) : aOrdonner ? (
        <ListeOrdonnable
          elements={carte.options}
          options={options}
          desactivee={repondu}
          onChangement={setChoisies}
        />
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {carte.options.map((option) => (
            <OptionPilule
              key={option.id}
              texte={option.content_md}
              options={options}
              choisie={choisies.includes(option.id)}
              etat={etatDeLOption(option.id, choisies, montre)}
              desactivee={repondu}
              // RG-2.2bis — une case à cocher DIT qu'on peut en prendre
              // plusieurs. Sans elle, la pilule d'un choix multiple ressemble
              // trait pour trait à celle d'un choix unique, et le candidat en
              // coche une puis attend qu'il se passe quelque chose.
              aCocher={multiple}
              onClick={() => {
                choisir(option.id)
              }}
            />
          ))}
        </div>
      )}

      {(aOrdonner || aApparier) && !repondu ? (
        <button
          type="button"
          // **Cliquable seulement quand tout est placé** — §150. Valider un
          // appariement à moitié rempli fait perdre des points sur le format et
          // non sur la connaissance ; l'ordonnancement, lui, est toujours
          // complet, chaque ligne occupant déjà un rang.
          disabled={aApparier && Object.keys(paires).length !== carte.options.length}
          onClick={() => {
            // L'ordre initial vaut réponse si le candidat n'a rien déplacé : il
            // peut le juger déjà correct, et exiger un mouvement pour valider
            // lui ferait déranger puis remettre.
            const donnee =
              aOrdonner && choisies.length > 0 ? choisies : carte.options.map((o) => o.id)

            setEnvoyee(true)
            setChoisies(donnee)
            // §150 — **le verdict est local ici aussi.** La carte porte
            // `key_order` et `key_pairs` quand elle a le droit de corriger ; sans
            // elles, un ordonnancement répondu hors connexion n'obtenait aucun
            // verdict, jamais.
            setLocal(sansVerdict ? null : corriger(donnee, carte, paires))
            onRepondre(composee(donnee), depuis(montreeA.current))
          }}
          style={{ background: 'var(--accent-fort)', color: 'var(--sur-accent)' }}
          className="mt-3 h-11 w-full rounded-full px-4 font-bold disabled:opacity-40"
        >
          {aApparier
            ? `Valider — ${String(Object.keys(paires).length)} sur ${String(carte.options.length)}`
            : 'Valider cet ordre'}
        </button>
      ) : null}

      {multiple && !repondu ? (
        <button
          type="button"
          // **Cliquable seulement au bon compte.** Valider trois réponses sur
          // quatre est faux par construction : laisser partir, c'est facturer
          // au candidat une erreur de format, pas de connaissance.
          disabled={repondu || (combienDeCles !== null && choisies.length !== combienDeCles)}
          onClick={() => {
            setEnvoyee(true)
            setLocal(sansVerdict ? null : corriger(choisies, carte, paires))
            onRepondre(composee(choisies), depuis(montreeA.current))
          }}
          style={{ background: 'var(--accent-fort)', color: 'var(--sur-accent)' }}
          className="mt-3 h-11 w-full rounded-full px-4 font-bold disabled:opacity-40"
        >
          {combienDeCles === null
            ? `Valider ${String(choisies.length)} réponse${choisies.length > 1 ? 's' : ''}`
            : `Valider — ${String(choisies.length)} sur ${String(combienDeCles)}`}
        </button>
      ) : null}

      {montre !== undefined ? (
        <Correction
          devoilement={montre}
          options={options}
          deplie={deplie}
        />
      ) : null}

      {/* **La réponse est prise, la correction attend le réseau** — RG-9.4,
          §111. Sans cette ligne, la carte se fermait sans rien dire et le
          candidat ne pouvait pas distinguer « enregistré » de « perdu ». */}
      {enFile && montre === undefined ? (
        <p role="status" className="mt-3 text-[13px]" style={{ color: 'var(--texte-2)' }}>
          Réponse enregistrée. La correction arrivera au retour du réseau.
        </p>
      ) : null}

      {/* RG-2.19 — marquer reste disponible avant comme après la réponse : le
          cas le plus fréquent est l'énoncé qu'on ne comprend pas et sur lequel
          on ne veut pas répondre au hasard. */}
      {onMarquer !== undefined ? (
        <ActionBar
          marque={marque}
          peutExpliquer={montre !== undefined}
          explicationOuverte={deplie}
          stats={carte.stats}
          onMarquer={onMarquer}
          onExpliquer={() => { setDeplie((avant) => !avant); }}
          onLecon={onLecon}
          onPartager={onPartager}
        />
      ) : null}
    </article>
  )
}

/**
 * L'horloge, tenue hors du composant.
 *
 * Un rendu React doit être PUR : `Date.now()` appelé dans le corps d'un
 * composant rendrait deux rendus successifs différents sans qu'aucun état
 * n'ait changé.
 */
function maintenant(): number {
  return Date.now()
}

/** Le temps écoulé depuis l'affichage — 0 si la carte vient d'apparaître. */
function depuis(debut: number | null): number {
  return debut === null ? 0 : maintenant() - debut
}

/**
 * Tout ce que la ligne-item met sous les yeux du candidat — §153.
 *
 * Le miroir exact de ce que `SqlFeedComposer` compte pour `has_math`, plus ce
 * qu'il ne PEUT pas compter : le dévoilement d'une évaluation notée, qui revient
 * après la réponse, et les raisons par option, qui n'existent que là.
 *
 * `expected_answer` en est absent volontairement : c'est la réponse attendue en
 * toutes lettres, affichée en texte nu parce que c'est ce que le candidat aurait
 * dû TAPER — y rendre du LaTeX lui montrerait une chose et en attendrait une
 * autre.
 */
function aRendre(carte: Carte, montre: Devoilement | undefined): (string | null | undefined)[] {
  return [
    carte.stem_md,
    ...carte.options.map((o) => o.content_md),
    ...(carte.appariements ?? []).map((a) => a.content_md),
    montre?.correction_note_md,
    montre?.explanation_md,
    ...(montre === undefined ? [] : Object.values(montre.rationales)),
  ]
}

/**
 * Le verdict que l'écran rend lui-même — ADR-06, DECISIONS §4.
 *
 * **`null` quand la carte ne porte pas ses clés.** C'est le cas des évaluations
 * notées : on ne devine pas un verdict, on n'en affiche aucun. En inventer un
 * par défaut serait pire que de faire attendre.
 *
 * La note de correction et l'explication restent absentes : elles arrivent avec
 * la réponse du serveur. Ce qu'on donne tout de suite, c'est ce qui coûte le
 * plus cher à attendre — la couleur et le mot.
 */
function corriger(
  choisies: number[],
  carte: Carte,
  paires: Record<string, string> = {},
): Devoilement | null {
  const cles = carte.key_option_ids

  if (cles === undefined) return null

  const juste = verdictLocal(choisies, carte, paires)

  if (juste === null) return null

  return {
    is_correct: juste,
    credit: juste ? 1 : 0,
    key_option_ids: cles,
    // **La correction vient de la CARTE, pas du réseau.** Elle a voyagé avec
    // elle ; aller la chercher après la réponse faisait attendre plusieurs
    // secondes le moment précis qui enseigne.
    correction_note_md: carte.correction?.note_md ?? null,
    explanation_md: carte.correction?.explication_md ?? null,
    rationales: {},
    // La réponse ATTENDUE en toutes lettres vient du serveur : l'écran connaît
    // la disposition juste, il ne sait pas la rédiger — et l'inventer ici
    // dupliquerait une mise en forme qui doit rester à un seul endroit.
    expected_answer: null,
  }
}

/**
 * Juste ou faux, selon le format — §150.
 *
 * `null` quand la carte ne porte pas de quoi trancher : on n'affiche alors
 * aucun verdict plutôt que d'en inventer un.
 */
function verdictLocal(
  choisies: number[],
  carte: Carte,
  paires: Record<string, string>,
): boolean | null {
  if (carte.type === 'ordonnancement') {
    const attendu = carte.key_order

    // **Les clés ne disent pas dans quel ORDRE.** Sans `key_order`, l'écran
    // n'avait rien à comparer : un ordonnancement répondu hors connexion
    // n'obtenait donc aucun verdict, jamais.
    if (attendu === undefined) return null

    return attendu.length === choisies.length && attendu.every((id, rang) => choisies[rang] === id)
  }

  if (carte.type === 'appariement') {
    const attendu = carte.key_pairs

    if (attendu === undefined) return null

    return carte.options.every((o) => {
      const donne = paires[String(o.id)]

      return donne !== undefined && donne === attendu[String(o.id)]
    })
  }

  const cles = carte.key_option_ids ?? []
  const toutes = cles.every((id) => choisies.includes(id))
  const aucuneDeTrop = choisies.every((id) => cles.includes(id))

  return toutes && aucuneDeTrop && choisies.length > 0
}

/**
 * Ce qu'on attend du candidat — RG-2.2bis.
 *
 * **Un QCM à quatre clés présenté comme un QCM simple se répond faux par
 * construction** : on coche la première clé qu'on reconnaît, on valide, et l'on
 * découvre qu'il en manquait trois. Ce n'est pas une mesure de ce qu'on sait,
 * c'est une mesure de ce qu'on a deviné du format.
 *
 * La consigne disparaît une fois la réponse donnée : elle a fait son travail,
 * et la place revient à la correction.
 */
function Consigne({
  type,
  combienDeCles,
  repondu,
}: {
  type: string
  combienDeCles: number | null
  repondu: boolean
}) {
  if (repondu) return null

  const dit =
    type === 'qcm_multiple'
      ? combienDeCles === null
        ? 'Choix multiple — plusieurs réponses sont justes.'
        : `Choix multiple — ${String(combienDeCles)} réponses à choisir.`
      : type === 'vrai_faux'
        ? 'Vrai ou faux.'
        : type === 'texte_court'
          ? 'Réponse courte à saisir.'
          : type === 'ordonnancement'
            // Le geste est dit, et la solution de repli avec : sur un écran qui
            // capte mal le glissement, savoir que les flèches existent évite de
            // renoncer à la question.
            ? 'Remettez dans l’ordre — glissez, ou utilisez les flèches.'
            : type === 'appariement'
              // §150 — le geste tient en une phrase, et il tient parce qu'il
              // n'y en a qu'un : on tape à gauche, puis on tape la réponse.
              ? 'Associez chaque ligne à sa réponse — touchez la ligne, puis la réponse.'
              : 'Une seule réponse.'

  return (
    <p className="mt-1 text-[13px]" style={{ color: 'var(--texte-2)' }}>
      {dit}
    </p>
  )
}

function Entete({ carte, onQuiz }: { carte: Carte; onQuiz?: (() => void) | undefined }) {
  // RG-2.1 — le chemin complet du tag principal. Le candidat doit savoir de
  // quoi on lui parle avant de lire l'énoncé.
  const chemin = carte.tag.path.split('.').join(' › ')

  return (
    <header
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]"
      style={{ color: 'var(--texte-2)' }}
    >
      {/* RG-2.38 — une carte mène à son tag en une tape. C'est le trajet qui
          transforme « cette question m'a surpris » en « je m'entraîne
          là-dessus », et il ne doit rien coûter. */}
      {onQuiz === undefined ? (
        <span>{chemin}</span>
      ) : (
        <button type="button" onClick={onQuiz} className="text-left underline-offset-2 hover:underline">
          {chemin}
        </button>
      )}

      {carte.provenance !== null ? <MarqueDeLArchive provenance={carte.provenance} /> : null}
    </header>
  )
}

/**
 * « Déjà tombé » — la marque d'une question réellement posée — RG-2.11,
 * [DECISIONS §227].
 *
 * **C'est l'argument le plus fort du produit, et il tenait dans un titre de
 * sujet.** La pastille affichait `paper_title` en entier — « Concours
 * d'admission, Mercredi 1er Octobre 2003 (1ère partie) » —, une ligne qui
 * poussait le fil d'ariane sur deux rangs et que personne ne lisait jusqu'au
 * bout. Ben : « un élément visuel minimaliste qui permet de voir qu'un item a
 * déjà été apparu dans les archives ».
 *
 * **L'année suffit à porter l'information.** Ce que le candidat veut savoir,
 * c'est que la question est tombée — et quand, pour juger si elle est encore
 * d'actualité. Le titre complet reste accessible : il est dans `title` pour la
 * souris, et lu en entier par les lecteurs d'écran.
 *
 * **Elle vaut partout où une carte paraît** — le Fil, les marque-pages, « à
 * revoir », les quiz de section, les échanges entre amis. Aucun de ces écrans
 * n'a rien à faire : ils passent tous par la même carte, et la provenance
 * arrive avec elle.
 *
 * **Le fond d'accent plutôt qu'une bordure** : la marque doit se distinguer du
 * fil d'ariane qui la précède, qui est du texte gris sur fond de page. Une
 * bordure de plus au milieu d'un chemin déjà ponctué de chevrons ne se voyait
 * pas.
 */
function MarqueDeLArchive({ provenance }: { provenance: NonNullable<Carte['provenance']> }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-bold"
      style={{ background: 'var(--surface-2)', color: 'var(--accent-fort)' }}
      title={provenance.label}
    >
      <Icone nom="document" taille={12} />
      {provenance.year}
      <span className="sr-only">— déjà tombé : {provenance.label}</span>
    </span>
  )
}

type EtatOption = 'neutre' | 'choisie' | 'juste' | 'ratee'

function etatDeLOption(id: number, choisies: number[], devoilement?: Devoilement): EtatOption {
  // Sans dévoilement — avant la réponse, ou pendant une épreuve notée — on
  // montre SEULEMENT ce que le candidat a coché. Il doit pouvoir relire son
  // choix ; il ne doit pas apprendre s'il est bon.
  if (devoilement === undefined) return choisies.includes(id) ? 'choisie' : 'neutre'

  if (devoilement.key_option_ids.includes(id)) return 'juste'

  return choisies.includes(id) ? 'ratee' : 'neutre'
}

/**
 * L'aplat de chaque état.
 *
 * `color-mix` tient la teinte du jeton — donc du thème et de l'accent choisis —
 * tout en restant assez pâle pour que le texte garde ses 4,5:1 (RG-D1). Écrire
 * une couleur en dur ici la ferait échapper au contrôle de contraste, qui ne
 * vérifie que la palette.
 */
const HABILLAGE: Record<EtatOption, { borderColor: string; background?: string; color?: string }> = {
  neutre: { borderColor: 'var(--bordure)' },
  choisie: {
    borderColor: 'var(--accent)',
    background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
  },
  juste: {
    borderColor: 'var(--juste)',
    background: 'color-mix(in srgb, var(--juste) 14%, transparent)',
  },
  // **Gris, et non rouge** (7 août 2026). Le rouge dit « faute » ; le gris dit
  // « pas celle-là ». Un candidat se trompe des dizaines de fois par séance, et
  // une séance qui saigne en rouge décourage avant d'enseigner. Ce qui doit
  // ressortir, c'est la BONNE réponse — c'est elle qu'on vient chercher.
  ratee: {
    // Plus prononcé que `surface-2`, qui se confondait avec le fond : on doit
    // VOIR ce qu'on a coché, sans que cela crie « faute ».
    borderColor: 'var(--bordure-champ)',
    background: 'color-mix(in srgb, var(--texte-2) 16%, transparent)',
    color: 'var(--texte-2)',
  },
}

function OptionPilule({
  texte,
  options,
  choisie,
  etat,
  desactivee,
  aCocher = false,
  onClick,
}: {
  texte: string
  options: RenduOptions
  choisie: boolean
  etat: EtatOption
  desactivee: boolean
  aCocher?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desactivee}
      aria-pressed={choisie}
      // `checkbox` quand plusieurs réponses sont attendues : le lecteur
      // d'écran annonce alors « case à cocher », ce qui est l'information
      // que la case donne à l'œil.
      role={aCocher ? 'checkbox' : undefined}
      aria-checked={aCocher ? choisie : undefined}
      // RG-D10 — pilule pleine largeur, bordure 1 px, 44 px de haut au
      // minimum : c'est la taille sous laquelle une cible devient difficile à
      // atteindre au pouce, et le Fil se joue au pouce.
      style={HABILLAGE[etat]}
      className="min-h-11 w-full rounded-full border px-4 py-2 text-left text-[15px] transition-colors duration-[120ms]"
    >
      <span className="flex items-center gap-3">
        {aCocher && <Case cochee={choisie} />}
        <RichText texte={texte} options={options} className="flex-1 [&_p]:my-0" />
      </span>
    </button>
  )
}

/**
 * La case d'un choix multiple.
 *
 * Carrée, et non ronde : le carré dit « plusieurs », le rond dit « une seule ».
 * C'est une convention que le candidat connaît de tous les formulaires qu'il a
 * remplis, et l'emprunter coûte moins que de l'expliquer.
 */
function Case({ cochee }: { cochee: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-5 shrink-0 items-center justify-center rounded border-2"
      style={{
        borderColor: cochee ? 'var(--accent-fort)' : 'var(--bordure-champ)',
        background: cochee ? 'var(--accent-fort)' : 'transparent',
        color: 'var(--sur-accent)',
      }}
    >
      {cochee && <Icone nom="coche" taille={13} />}
    </span>
  )
}

/**
 * Le verdict, en PASTILLE — RG-2.45.
 *
 * **Ben, le 8 septembre 2026 : « il doit y avoir des pastilles pour les bonnes
 * réponses ».** Le verdict n'était qu'un mot en gras, de la même taille que le
 * texte autour : sur un téléphone, entre l'énoncé et l'explication, il se
 * perdait. Une pastille se voit sans se lire.
 *
 * **Le fond porte le sens, l'icône le confirme.** Un mot coloré demande de
 * distinguer un vert d'un rouge — ce que quatre hommes sur cent ne font pas.
 * La coche et la croix disent la même chose sans la couleur, et c'est pour cela
 * qu'elles sont là.
 *
 * **Les deux verdicts ont leur pastille**, et ce n'est pas de la symétrie
 * décorative : n'en donner qu'au juste ferait du faux une absence, alors que
 * RG-P50 pose qu'une réponse fausse ne retire rien. Le faux se distingue par sa
 * TEINTE, jamais par son poids.
 */
function PastilleDuVerdict({ juste }: { juste: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-bold"
      style={{
        background: juste ? 'var(--juste)' : 'var(--faux)',
        color: juste ? 'var(--sur-juste)' : 'var(--sur-faux)',
      }}
    >
      <Icone nom={juste ? 'coche' : 'croix'} taille={15} />
      {juste ? 'Juste' : 'Faux'}
    </span>
  )
}

function Correction({
  devoilement,
  options,
  deplie,
}: {
  devoilement: Devoilement
  options: RenduOptions
  deplie: boolean
}) {
  const details = devoilement.explanation_md
  const pourquoi = Object.values(devoilement.rationales)

  return (
    <div className="mt-3">
      <PastilleDuVerdict juste={devoilement.is_correct} />

      {/* Un format à saisie n'a aucune option à mettre en évidence : sans
          cette ligne, le candidat qui s'est trompé ne saurait toujours pas ce
          qu'il fallait écrire. */}
      {devoilement.expected_answer !== null ? (
        <p className="mt-1 text-[13px]" style={{ color: 'var(--texte-2)' }}>
          Réponse attendue :{' '}
          <span style={{ color: 'var(--texte-1)' }}>{devoilement.expected_answer}</span>
        </p>
      ) : null}

      {/* RG-04-30 — le plancher éditorial. Le candidat doit comprendre son
          erreur SUR LA CARTE, à 23 h, sur un forfait compté. */}
      {devoilement.correction_note_md !== null ? (
        <div className="mt-1 text-[13px]" style={{ color: 'var(--texte-2)' }}>
          <RichText texte={devoilement.correction_note_md} options={options} />
        </div>
      ) : null}

      {/* **Aucun bouton « Détails » ici.** C'est l'ampoule de la barre d'actions
          qui ouvre l'explication, et elle seule : un second déclencheur juste
          sous la note de correction poussait déjà la carte suivante hors de
          l'écran, et donnait deux façons de faire la même chose. L'explication
          reste absente du DOM tant qu'on ne l'a pas demandée (RG-2.4ter). */}
      {deplie && (details !== null || pourquoi.length > 0) ? (
        <div
          className="mt-2 flex flex-col gap-2 text-[13px]"
          style={{ color: 'var(--texte-2)' }}
        >
          {details !== null ? <RichText texte={details} options={options} /> : null}

          {pourquoi.map((raison, rang) => (
            <RichText key={`r${String(rang)}`} texte={raison} options={options} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
